import React from 'react';

import { type ComputedContentSourceDocument, hashComputedContentSource } from '@gitbook/hive-core';
import type { Document } from '@gitbook/slate';
import { useStable } from '@gitbook/util-react-hooks';

import { useHiveClient } from './useHiveClient';

/**
 * Compute a document for a source and returns it.
 * This hook will suspend if the document is not computed yet.
 */
export function useHiveComputedDocument({
    contentId,
    computedSeed,
    computedSource,
}: {
    contentId: string;
    computedSeed: string;
    computedSource: ComputedContentSourceDocument;
}): Document {
    const hiveClient = useHiveClient();

    // Fetch the initial document
    const contentComputer = hiveClient.getContentComputer(contentId);
    const documentPromise = contentComputer.getCompleteDocument({
        computedSeed,
        computedSource,
    });
    if (documentPromise instanceof Promise) {
        throw documentPromise;
    }

    // Subscribe to the document changes
    const [document, setDocument] = React.useState<Document>(documentPromise.document);
    const stableComputedSource = useStable(
        computedSource,
        (a, b) => hashComputedContentSource(a) === hashComputedContentSource(b)
    );
    React.useEffect(() => {
        const stream = contentComputer.computeDocument({
            input: {
                computedSeed,
                computedSource: stableComputedSource,
            },
            onUpdate: (result) => {
                if (result.result) {
                    setDocument(result.result.document);
                }
            },
        });

        if (stream.result.result) {
            setDocument(stream.result.result.document);
        }

        return () => {
            stream.cancel();
        };
    }, [contentComputer, computedSeed, stableComputedSource]);

    return document;
}
